import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import OpenInNew from '@mui/icons-material/OpenInNew';

const GOOGLE_DEPRECATION_DOCS_URL =
  'https://developers.google.com/workspace/admin/email-audit/download-mailbox';
const CONTACT_CSM_URL = 'https://gpanel.io/client-success';
const VAULT_PATH = `/${pagesConfig.vaultExport.link}`;

interface Props {
  emailAuditApiAvailable: boolean;
  hasVaultAccess: boolean;
}

export default function GmailExportDeprecationBanner({
  emailAuditApiAvailable,
  hasVaultAccess,
}: Props) {
  const learnMoreLink = (
    <MuiLink
      href={GOOGLE_DEPRECATION_DOCS_URL}
      target="_blank"
      rel="noreferrer"
      color="inherit"
      sx={{
        fontWeight: 'bold',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        verticalAlign: 'middle',
      }}
    >
      Learn More <OpenInNew sx={{ fontSize: 14 }} />
    </MuiLink>
  );

  if (!hasVaultAccess) {
    return (
      <Alert
        severity="warning"
        sx={{
          mb: 1,
          border: (theme) =>
            `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        }}
        action={
          <Button
            href={CONTACT_CSM_URL}
            target="_blank"
            rel="noreferrer"
            color="inherit"
            variant="outlined"
            size="small"
          >
            Contact CSM
          </Button>
        }
      >
        <AlertTitle>Email Audit API is deprecated.</AlertTitle>
        Google is phasing out the Email Audit API. To avoid future service
        interruptions, we recommend transitioning your workflows to Vault.{' '}
        {learnMoreLink}
      </Alert>
    );
  }

  if (!emailAuditApiAvailable) {
    return (
      <Alert
        severity="error"
        sx={{
          mb: 1,
          border: (theme) =>
            `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
        }}
        action={
          <Button
            component={Link}
            to={VAULT_PATH}
            color="inherit"
            variant="outlined"
            size="small"
          >
            Try Vault
          </Button>
        }
      >
        <AlertTitle>Email Audit API is deprecated.</AlertTitle>
        Google is phasing out the Email Audit API. We are currently seeing
        errors with standard exports due to Google API limitations. Please use
        the Vault tool for better reliability. {learnMoreLink}
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      sx={{
        mb: 1,
        border: (theme) =>
          `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
      }}
      action={
        <Button
          component={Link}
          to={VAULT_PATH}
          color="inherit"
          variant="outlined"
          size="small"
        >
          Try Vault
        </Button>
      }
    >
      <AlertTitle>Email Audit API is deprecated.</AlertTitle>
      Google is phasing out the Email Audit API. To avoid future service
      interruptions, we recommend transitioning your workflows to Vault.{' '}
      {learnMoreLink}
    </Alert>
  );
}
